import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, X, Lock } from "lucide-react";

// ⚠️ AJUSTE CE CHEMIN selon l'emplacement réel du composant Admin dans ton projet
// (le back-office hôtelier existant — celui rendu sur la route /admin)
import Admin from "../adminHotel/pages/Admin";

const API = "https://api.komorotel.com/api/superadmin";

/**
 * Mode Vision — route /superadmin/vision/:hotelId
 *
 * Le cookie admin_token avec flag isSuperAdminView a déjà été posé par
 * POST /superadmin/hotels/:id/vision avant la navigation vers cette page.
 *
 * Côté backend, verifyAdmin détecte isSuperAdminView et bloque toute
 * requête non-GET (403 "lecture seule") — la protection est donc serveur.
 * Côté frontend, on superpose :
 *   1. une bannière persistante "Mode Vision — Lecture seule"
 *   2. un intercepteur axios qui annule proprement les écritures
 *      (évite les erreurs 403 brutes dans l'UI de l'hôtelier)
 *   3. un voile CSS désactivant visuellement les boutons d'action destructifs
 */
export default function VisionMode() {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const [hotelName, setHotelName] = useState("");
  const [blockedToast, setBlockedToast] = useState(false);

  // Récupère le nom de l'hôtel pour la bannière
  useEffect(() => {
    axios.get(`${API}/hotels/${hotelId}`, { withCredentials: true })
      .then(r => setHotelName(r.data?.hotel?.name || r.data?.name || ""))
      .catch(() => setHotelName(""));
  }, [hotelId]);

  // Intercepteur axios : bloque les écritures côté client (le backend bloque déjà)
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const method = (config.method || "get").toLowerCase();
      const isWrite = ["post", "put", "patch", "delete"].includes(method);
      const isAdminApi = (config.url || "").includes("api.komorotel.com");
      const isVisionApi = (config.url || "").includes("/superadmin/");

      if (isWrite && isAdminApi && !isVisionApi) {
        setBlockedToast(true);
        setTimeout(() => setBlockedToast(false), 2500);
        // Annulation propre de la requête
        const controller = new AbortController();
        controller.abort();
        return { ...config, signal: controller.signal };
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  const handleExit = () => {
    // Retour à la liste des hôtels — le cookie admin_token (4h) expirera seul,
    // et le cookie superadmin_token est toujours actif.
    navigate("/superadmin/hotels");
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>

      {/* ── BANNIÈRE MODE VISION (persistante) ── */}
      <div className="sticky top-0 z-[100] flex items-center justify-between gap-3 px-4 py-2.5 bg-gray-900 text-white shadow-sm">
        <div className="flex items-center gap-2.5 min-w-0">
          <Eye size={16} className="text-[#7BC85F] shrink-0" />
          <p className="text-sm truncate">
            <span className="font-semibold">Mode Vision</span>
            {hotelName && <span className="text-gray-300"> — {hotelName}</span>}
            <span className="hidden sm:inline text-gray-400"> · Lecture seule, aucune modification possible</span>
          </p>
        </div>
        <button
          onClick={handleExit}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-medium transition shrink-0"
        >
          <X size={13} />
          Quitter
        </button>
      </div>

      {/* ── TOAST "ACTION BLOQUÉE" ── */}
      {blockedToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[110] flex items-center gap-2 px-4 py-3 bg-gray-900 text-white text-sm rounded-xl shadow-lg">
          <Lock size={14} className="text-[#7BC85F]" />
          Mode Vision : les modifications sont désactivées.
        </div>
      )}

      {/* ── BACK-OFFICE HÔTELIER (UI Admin existante) ── */}
      {/* Le CSS ci-dessous neutralise visuellement les contrôles d'écriture
          courants sans toucher au code d'Admin.jsx. La vraie protection
          reste le backend (verifyAdmin → 403 sur non-GET). */}
      <style>{`
        .vision-readonly button[type="submit"],
        .vision-readonly input[type="submit"] {
          opacity: 0.5;
          pointer-events: none;
        }
      `}</style>
      <div className="flex-1 vision-readonly">
        <Admin />
      </div>
    </div>
  );
}
